import React from 'react';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { Icon } from '@/shared/icons';

export interface ChannelMultiSelectProps {
  selectedChannelIds: string[];
  onSelectionChange: (channelIds: string[]) => void;
}

export const ChannelMultiSelect: React.FC<ChannelMultiSelectProps> = ({
  selectedChannelIds,
  onSelectionChange,
}) => {
  // Usar endpoint dropdown (sem passar por middleware de permissões)
  const { data: channels, isLoading } = useChannelsDropdown();

  const toggleChannel = (channelId: string) => {
    const isSelected = selectedChannelIds.includes(channelId);
    if (isSelected) {
      onSelectionChange(selectedChannelIds.filter((id) => id !== channelId));
    } else {
      onSelectionChange([...selectedChannelIds, channelId]);
    }
  };

  if (isLoading) {
    return (
      <div
        className="px-4 py-3 rounded-lg border text-sm text-center"
        style={{
          backgroundColor: 'var(--theme-input-background)',
          borderColor: 'var(--theme-input-border)',
          color: 'var(--theme-muted-text)',
        }}
      >
        Carregando canais...
      </div>
    );
  }

  if (!channels || channels.length === 0) {
    return (
      <div
        className="px-4 py-3 rounded-lg border text-sm text-center"
        style={{
          backgroundColor: 'var(--theme-input-background)',
          borderColor: 'var(--theme-input-border)',
          color: 'var(--theme-muted-text)',
        }}
      >
        Nenhum canal cadastrado
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border max-h-60 overflow-y-auto"
      style={{
        backgroundColor: 'var(--theme-input-background)',
        borderColor: 'var(--theme-input-border)',
      }}
    >
      {channels.map((channel) => {
        const isSelected = selectedChannelIds.includes(channel.id);
        return (
          <div
            key={channel.id}
            className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b last:border-b-0"
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={0}
            style={{
              borderColor: 'var(--theme-input-border)',
            }}
            onClick={() => toggleChannel(channel.id)}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleChannel(channel.id); } }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <div
              className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
              style={{
                borderColor: isSelected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
                backgroundColor: isSelected ? 'var(--theme-primary)' : 'transparent',
              }}
            >
              {isSelected && <Icon name="check" className="w-3 h-3 text-white" />}
            </div>
            <div className="flex-1">
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {channel.name}
              </p>
            </div>
            <span
              className="px-2 py-1 rounded-full text-xs"
              style={{
                backgroundColor: channel.status === 'active'
                  ? 'var(--theme-success-background)'
                  : 'var(--theme-error-background)',
                color: channel.status === 'active'
                  ? 'var(--theme-success-color)'
                  : 'var(--theme-error-color)',
              }}
            >
              {channel.status === 'active' ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        );
      })}
    </div>
  );
};
